<script setup lang="ts">
import {
  PopoverColorEnum,
  Tab,
  Tabs,
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
  TirPmModal,
  TirPmNotificationEventBus,
  TirPmNotificationModel,
  TirPmPopover,
} from "tir-components";
import { KeyIcon, PlusIcon } from "tir-style-system/icons/outline";
import { computed, onBeforeMount, onMounted, PropType, ref, watch } from "vue";
import { RouterView, useRouter } from "vue-router";
import { WeeekTabsEnum } from "./enums";
import { AdapterService } from "./adapter";
import { GetAttributeOptionsResponseItem, WeeekDataModel } from "@/models";
import {
  UpsertAttributesOptionsRequestItem,
  WeeekOptionsUpsertPrioritiesRequest,
  WeeekOptionsUpsertStatusesRequest,
  WeeekOptionsUpsertTagsRequest,
} from "./models";
import UpsertTagAttributeOptionsRequestItem from "@/api/Weeek/dataSource/api/project-manager/weeek/i-services/models/request/UpsertTagAttributeOptionsRequestItem";
import GetAttributeTagOptionsResponseItem from "@/api/Weeek/dataSource/api/project-manager/weeek/i-services/models/response/GetAttributeTagOptionsResponseItem";
import { TagModel } from "@/components/task-forms/models";

/**Роутер */
const router = useRouter();
/**Адаптер */
const adapter = new AdapterService();

/**Параметры */
const props = defineProps({
  /**апи ключ */
  apiKey: { type: String },
  /**id проекта */
  projectId: { type: Number },
  /**Привязан ли токен */
  isTokenLinked: { type: Boolean },
  /**Данные weeek */
  dataWeeek: { type: Object as PropType<WeeekDataModel> },
  /**Есть ли изменения в массиве статусов Weeek */
  changeDataWeeek: { type: Boolean },
  /**Теги проекта ПМ */
  tagsProjectPm: { type: Array as PropType<TagModel[]> },
});
/**События */
const emit = defineEmits<{
  /**Обновление апи ключа */
  updateApiKey: [value: string];
  /**Обновление настроек проекта */
  updateProjectSettings: [];
  /**Обновление оригинальные данные weeek */
  updateOriginalDataWeeek: [];
  /**Обновить список */
  updateList: [];
  /**Поиск тегов */
  searchTags: [value: string];
}>();

/**Состояние таба */
const stateTabs = ref();
/**Видимость модалки */
const isVisibleModal = ref(false);
/**Выйти из модалки с переходом на другую вкладку */
const desiredRoute = ref();
/**Выйти из модалки */
const isExitModal = ref(false);
/**Загрузка */
const isLoading = ref(false);

router.beforeEach((to, from, next) => {
  if (props.changeDataWeeek && !isVisibleModal.value && !isExitModal.value) {
    isVisibleModal.value = true;
    const currentTab = Object.values(WeeekTabsEnum).find((tab) => tab === from.name);
    stateTabs.value = currentTab;
    desiredRoute.value = to;
    next(false);
  } else {
    isExitModal.value = false;
    next();
  }
});

/**При вмонтировании компонента */
onMounted(async () => {
  stateTabs.value = router.currentRoute.value.name;
});

/**Отключить модалку */
const hideModal = () => {
  isVisibleModal.value = false;
};
/**Переход по желаемому пути */
const goDesiredRoute = () => {
  isExitModal.value = true;
  isVisibleModal.value = false;
  const currentTab = Object.values(WeeekTabsEnum).find((tab) => tab === desiredRoute.value.name);
  stateTabs.value = currentTab;
  emit("updateList");
  router.push({ name: desiredRoute.value.name });
};
/**Добавить оповещение */
const addNotification = (message: string, isError: boolean = false, life?: number) => {
  TirPmNotificationEventBus.emit(
    "add",
    new TirPmNotificationModel({
      title: message,
      type: isError ? "error" : "success",
      group: "general",
      life: life ?? 2000,
      closable: true,
    }),
  );
};
/**Добавление/удаление/изменение сопоставления статусов */
const upsertStatusesWeeek = async () => {
  const response = await adapter.upsertStatusesWeeek(
    new WeeekOptionsUpsertStatusesRequest({
      ProjectId: props.projectId,
      Attributes: props.dataWeeek.StatusesWeeek.map(
        (item) => new GetAttributeOptionsResponseItem(item),
      ),
    }),
  );
  if (response.IsSuccess) {
    emit("updateOriginalDataWeeek");
    addNotification("Данные сохранены");
  }
};
/**Добавление/удаление/изменение сопоставления приоритетов */
const upsertPrioritiesAsync = async () => {
  const response = await adapter.upsertPrioritiesAsync(
    new WeeekOptionsUpsertPrioritiesRequest({
      ProjectId: props.projectId,
      Attributes: props.dataWeeek.PrioritiesWeeek.map(
        (item) => new GetAttributeOptionsResponseItem(item),
      ),
    }),
  );
  if (response.IsSuccess) {
    emit("updateOriginalDataWeeek");
    addNotification("Данные сохранены");
  }
};
/**Добавление/удаление/изменение сопоставления типов */
const upsertTypesAsync = async () => {
  const response = await adapter.upsertTypesAsync(
    new WeeekOptionsUpsertPrioritiesRequest({
      ProjectId: props.projectId,
      Attributes: props.dataWeeek.TypesWeeek.map(
        (item) => new GetAttributeOptionsResponseItem(item),
      ),
    }),
  );
  if (response.IsSuccess) {
    emit("updateOriginalDataWeeek");
    addNotification("Данные сохранены");
  }
};
/**Добавление/удаление/изменение сопоставления тегов */
const upsertTagsAsync = async () => {
  const response = await adapter.upsertTagsAsync(
    new WeeekOptionsUpsertTagsRequest({
      ProjectId: props.projectId,
      Attributes: props.dataWeeek.TagsWeeek.map(
        (item) => new UpsertTagAttributeOptionsRequestItem(item),
      ),
    }),
  );
  if (response.IsSuccess) {
    emit("updateOriginalDataWeeek");
    addNotification("Данные сохранены");
  }
};
/**Добавить сопоставление статусов */
const addStatusesWeeek = async () => {
  props.dataWeeek.StatusesWeeek.push(
    new GetAttributeOptionsResponseItem({
      Id: null,
      WeeekValue: "",
      PmValue: "",
    }),
  );
};
/**Добавить сопоставление приоритетов */
const addPrioritiesWeeek = async () => {
  props.dataWeeek.PrioritiesWeeek.push(
    new GetAttributeOptionsResponseItem({
      Id: null,
      WeeekValue: "",
      PmValue: "",
    }),
  );
};
/**Добавить сопоставление типов */
const addTypesWeeek = async () => {
  props.dataWeeek.TypesWeeek.push(
    new GetAttributeOptionsResponseItem({
      Id: null,
      WeeekValue: "",
      PmValue: "",
    }),
  );
};
/**Добавить сопоставление тегов */
const addTagsWeeek = async () => {
  props.dataWeeek.TagsWeeek.push(
    new GetAttributeTagOptionsResponseItem({
      TagId: null,
      WeeekTagName: "",
    }),
  );
};
/**Сохранить изменения в атрубетах weeek */
const saveСhangesAttributeWeeek = async () => {
  isLoading.value = true;
  switch (stateTabs.value) {
    case WeeekTabsEnum.Statuses:
      await upsertStatusesWeeek();
      break;
    case WeeekTabsEnum.Priorities:
      await upsertPrioritiesAsync();
      break;
    case WeeekTabsEnum.Types:
      await upsertTypesAsync();
      break;
    case WeeekTabsEnum.Tags:
      await upsertTagsAsync();
      break;
  }
  isLoading.value = false;
};
/**Добавить новый элемент в атрибуты weeek */
const addNewAttributeWeeek = () => {
  switch (stateTabs.value) {
    case WeeekTabsEnum.Statuses:
      addStatusesWeeek();
      break;
    case WeeekTabsEnum.Priorities:
      addPrioritiesWeeek();
      break;
    case WeeekTabsEnum.Types:
      addTypesWeeek();
      break;
    case WeeekTabsEnum.Tags:
      addTagsWeeek();
      break;
  }
};
/**Смена таба */
const changeTab = (tab: WeeekTabsEnum) => {
  stateTabs.value = tab;
  switch (tab) {
    case WeeekTabsEnum.SettingsToken:
      router.push({ name: WeeekTabsEnum.SettingsToken });
      break;
    case WeeekTabsEnum.Statuses:
      router.push({ name: WeeekTabsEnum.Statuses });
      break;
    case WeeekTabsEnum.Priorities:
      router.push({ name: WeeekTabsEnum.Priorities });
      break;
    case WeeekTabsEnum.Tags:
      router.push({ name: WeeekTabsEnum.Tags });
      break;
    case WeeekTabsEnum.Types:
      router.push({ name: WeeekTabsEnum.Types });
      break;
  }
};
</script>
<template>
  <div>
    <div class="project-settings-weeek-page__tabs">
      <Tabs v-model="stateTabs">
        <Tab
          label="Настройки токена"
          :val="WeeekTabsEnum.SettingsToken"
          :currentTab="stateTabs"
          @click="changeTab(WeeekTabsEnum.SettingsToken)"
        >
          <KeyIcon class="key-icon" :class="{ 'key-icon-success': isTokenLinked }" />
        </Tab>
        <Tab
          label="Статусы"
          :val="WeeekTabsEnum.Statuses"
          :currentTab="stateTabs"
          :class="{ disabled: !isTokenLinked }"
          @click="changeTab(WeeekTabsEnum.Statuses)"
        >
          <div class="project-settings-weeek-page__count-elements">
            {{ dataWeeek.StatusesWeeek.length }}
          </div>
        </Tab>
        <Tab
          label="Приоритеты"
          :val="WeeekTabsEnum.Priorities"
          :currentTab="stateTabs"
          :class="{ disabled: !isTokenLinked }"
          @click="changeTab(WeeekTabsEnum.Priorities)"
        >
          <div class="project-settings-weeek-page__count-elements">
            {{ dataWeeek.PrioritiesWeeek.length }}
          </div>
        </Tab>
        <Tab
          label="Типы"
          :val="WeeekTabsEnum.Types"
          :currentTab="stateTabs"
          :class="{ disabled: !isTokenLinked }"
          @click="changeTab(WeeekTabsEnum.Types)"
        >
          <div class="project-settings-weeek-page__count-elements">
            {{ dataWeeek.TypesWeeek.length }}
          </div>
        </Tab>
        <Tab
          label="Теги"
          :val="WeeekTabsEnum.Tags"
          :currentTab="stateTabs"
          :class="{ disabled: !isTokenLinked }"
          @click="changeTab(WeeekTabsEnum.Tags)"
        >
          <div class="project-settings-weeek-page__count-elements">
            {{ dataWeeek.TagsWeeek.length }}
          </div>
          <TirPmPopover :color="PopoverColorEnum.Black" isTooltip>
            <template #content>sdcscsdc</template>
          </TirPmPopover>
        </Tab>
      </Tabs>
      <div
        class="project-settings-weeek-page__controllers"
        v-if="stateTabs !== WeeekTabsEnum.SettingsToken"
      >
        <TirPmButton
          :variant="TirPmButtonVariantEnum.SecondaryState"
          :size="TirPmButtonSizeEnum.Middle"
          @click="addNewAttributeWeeek"
        >
          <template #leftSlot>
            <PlusIcon />
          </template>
          Добавить
        </TirPmButton>
        <TirPmButton
          v-if="changeDataWeeek"
          :variant="TirPmButtonVariantEnum.PrimaryState"
          :size="TirPmButtonSizeEnum.Middle"
          :isLoading="isLoading"
          @click="saveСhangesAttributeWeeek()"
        >
          Сохранить
        </TirPmButton>
      </div>
    </div>
    <RouterView
      :projectId="projectId"
      :apiKey="apiKey"
      :dataWeeek="dataWeeek"
      :tagsProjectPm="tagsProjectPm"
      @searchTags="emit('searchTags', $event)"
      @updateApiKey="emit('updateApiKey', $event)"
      @updateProjectSettings="emit('updateProjectSettings')"
    />
  </div>
  <TirPmModal :isVisible="isVisibleModal" :isWithBack="false" title="Выйти сейчас?">
    <template #content>
      <div class="project-settings-weeek-page__modal-content">
        Если выйти сейчас, данные не сохранятся
      </div>
    </template>
    <template #footer>
      <TirPmButton :variant="TirPmButtonVariantEnum.PrimaryState" @click="hideModal">
        Вернуться к редактированию
      </TirPmButton>
      <TirPmButton :variant="TirPmButtonVariantEnum.LinkState" @click="goDesiredRoute">
        Выйти
      </TirPmButton>
    </template>
  </TirPmModal>
</template>
<style scoped lang="scss">
.project-settings-weeek-page {
  &__tabs {
    display: flex;
    align-items: center;
    justify-content: space-between;
    white-space: nowrap;
    margin-bottom: 1.5rem;
  }

  &__modal-content {
    color: var(--text-secondary, #686c73);
    font-size: 1rem;
  }

  &__controllers {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    margin-left: 1rem;
  }

  &__count-elements {
    color: var(--text-tertiary, #93969b);
    font-size: 1rem;
    font-weight: 400;
  }
}
.disabled {
  pointer-events: none;
  opacity: 0.5;
}
.key-icon {
  width: 1rem;
  color: var(--status-negative, #ec2e14);
  margin-left: 0.438rem;
}
.key-icon-success {
  color: var(--text-info, #2a77ef);
}
</style>
